import UserAvatar16 from '@carbon/icons-react/es/user--avatar/16';
import { GridApi } from 'ag-grid-community';
import { Dropdown } from 'antd';
import uniqBy from 'lodash/uniqBy';
import React, { useCallback, useMemo } from 'react';
import { FullScreenHandle } from 'react-full-screen';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ProjectId, StatusUpdateId, Team, User, UserType } from 'api/models';
import { CustomerPin } from 'api/spotlight/types';
import {
  StatusUpdate,
  StatusUpdateState,
  StatusUpdateStatuses,
  UserStatusUpdateStatuses,
} from 'api/status-updates';
import { CopyUrlToClipboard } from 'components/atoms/copy-to-clipboard';
import { MoreActions } from 'components/atoms/delete-in-more-actions';
import { Menu } from 'components/atoms/dropdown';
import { Form } from 'components/atoms/form';
import { PrivatePlacard } from 'components/atoms/private-placard';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { Tag } from 'components/atoms/tag';
import { SquadPicker } from 'components/molecules/squad-picker/squad-picker';
import { Trigger } from 'components/molecules/squad-picker/trigger';
import { SquadPayload } from 'components/molecules/squad-picker/types';
import { useProjectSquad } from 'components/molecules/squad-picker/use-squad-options';
import { userOption } from 'components/molecules/squad-picker/utils';
import { getStatusData } from 'components/organisms/projects/project-list/constants';
import { SpotlightGroup } from 'components/organisms/spotlight/spotlight-in-tag';
import {
  deleteStatusUpdate,
  putStatusUpdates,
  selectActiveStatusUpdate,
  setActiveStatusUpdate,
  setActiveStatusUpdateTitle,
  updateStatusUpdateTitle,
} from 'data/status-updates';
import { useGetAllActiveUsersMap } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useGetStatusUpdateReadStatusQuery } from 'queries/status-updates';
import { getTextFromHTML } from 'utils/getTextFromHTML';
import { getProjectStatusUpdatesUrl } from 'utils/urls';
import { AiFillPlaceholderSkeleton } from 'vendor/ckeditor/react-components/ai-fill/ai-fill-placeholder';
import { StatusUpdateCC } from '../carbon-copy';
import { CcEmailVal } from '../editor';
import styles from './editor-head.module.css';
import { StatusUpdateFullscreenToggle } from './fullscreen';
import { SentToInfo } from './sent-to-info';
import {
  AddMailWrapper,
  AddStatusButton,
  CCLabel,
  CloseIcon,
  CollaboratorsTriggerButton,
  EditorContainer,
  EditorHeading,
  FlexWrapper,
  FormItem,
  Label,
  SpotlightWrapper,
  SubHeader,
  Wrapper,
} from './styles';
import { SUAssigneePicker } from './su-assignee-picker';

export const EditorHead = ({
  onAsyncOperationSuccess,
  gridApi,
  userEmails,
  setUserEmails,
  handleFullScreen,
  canDelete,
  canEdit,
  projectId: projectIdAsNumber,
  canCreate,
  onPostDelete,
  isAiFillResolving,
  spotlights,
  onSpotlightClick,
}: {
  onAsyncOperationSuccess: (info: { title: string; message: string }) => void;
  gridApi: GridApi | null;
  userEmails: CcEmailVal[];
  setUserEmails: (users) => void;
  handleFullScreen: FullScreenHandle;
  projectId: number;
  canDelete?: boolean;
  canEdit?: boolean;
  canCreate?: boolean;
  onPostDelete?: VoidFunction;
  isAiFillResolving?: boolean;
  spotlights: CustomerPin[];
  onSpotlightClick?: (pin: CustomerPin) => void;
}): JSX.Element => {
  const users = useGetAllActiveUsersMap();
  const history = useHistory();
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate)!;

  const currentStatus = getStatusData(
    StatusUpdateStatuses,
    activeStatusUpdate?.status?.statusValue,
  );
  const dispatch = useDispatch();

  const isPublished = activeStatusUpdate?.state === StatusUpdateState.Published;

  const projectId = ProjectId(projectIdAsNumber);
  const { data: squad } = useProjectSquad(projectId);

  const userInfo = useUser();
  const user = userInfo && userInfo.user;

  let fullCollaboratorsList =
    squad.users?.filter((usr) => {
      return (
        usr.userId !== user.userId &&
        usr.userId !== activeStatusUpdate?.createdBy?.userId
      );
    }) || [];

  if (activeStatusUpdate?.private) {
    fullCollaboratorsList =
      fullCollaboratorsList?.filter(
        (usr) => usr.userType === UserType.Native,
      ) || [];
  }
  const collabrs =
    (activeStatusUpdate?.collaborators.map((usr) => {
      return users[usr.userId];
    }) as User[]) || [];

  const activeCollaborators = uniqBy(collabrs, 'userId');

  const documentTitlePlaceholder = useTranslation('untitled');
  const addCollaborators = useTranslation('status_updates.add.collaborators');
  const deletedToastTitle = useTranslation('status_updates.deleted_msg_title');
  const deletedToastMessage = useTranslation('status_updates.deleted_msg_desc');
  const translatedDeleteConfirmationMessageTitle = useTranslation(
    'status_updates.delete_su_alert_title',
  );
  const translatedDeleteConfirmationMessageContent = useTranslation(
    'status_updates.delete_su_alert_content',
  );

  const addStatus = useTranslation('su.add.status');
  const subject = useTranslation('su.email.subject');
  const to = useTranslation('su.email.To');
  const Cc = useTranslation('su.email.cc');

  const toUsers = useMemo(
    () => (activeStatusUpdate.toUsers ?? []).map((u) => userOption(u)),
    [activeStatusUpdate.toUsers],
  );

  const showDeleteSuccessToast = useCallback(() => {
    onAsyncOperationSuccess({
      title: deletedToastTitle,
      message: deletedToastMessage,
    });

    if (onPostDelete != null) {
      onPostDelete();
    } else {
      history.replace(getProjectStatusUpdatesUrl(projectId));
    }
  }, [
    onAsyncOperationSuccess,
    deletedToastTitle,
    deletedToastMessage,
    onPostDelete,
    history,
    projectId,
  ]);

  const updateActiveStatusUpdate = useCallback(
    (statusUpdate: StatusUpdate) => {
      dispatch(
        putStatusUpdates({
          statusUpdate,
        }),
      );
    },
    [dispatch],
  );

  useGetStatusUpdateReadStatusQuery(
    StatusUpdateId(activeStatusUpdate.statusUpdateId),
    {
      enabled: !activeStatusUpdate.seenByUser,
      onSuccess: () => {
        dispatch(
          setActiveStatusUpdate({
            ...activeStatusUpdate,
            seenByUser: true,
          }),
        );

        const row = gridApi?.getRowNode(
          String(activeStatusUpdate.statusUpdateId),
        );

        if (row) {
          row?.setData({
            ...activeStatusUpdate,
            seenByUser: true,
          });
          gridApi?.refreshCells({
            rowNodes: [row],
            force: true,
          });
        }
      },
    },
  );

  const handleMenuClick = (e) => {
    const status = StatusUpdateStatuses[e.key] as any;

    const update = {
      ...(activeStatusUpdate as StatusUpdate),
      status: {
        statusId: status.value,
        statusValue: status.label,
      },
    };

    dispatch(setActiveStatusUpdate(update));
    updateActiveStatusUpdate(update);
  };

  const handleToUsers = useCallback(
    (usr: User[]) => {
      const update = {
        ...activeStatusUpdate,
        toUsers: usr,
      };

      dispatch(setActiveStatusUpdate(update));
      updateActiveStatusUpdate(update);
    },
    [activeStatusUpdate, dispatch, updateActiveStatusUpdate],
  );

  const removeStatus = () => {
    const update = {
      ...activeStatusUpdate,
      status: {
        statusId: UserStatusUpdateStatuses.none.value,
        statusValue: UserStatusUpdateStatuses.none.label,
      },
    };

    dispatch(setActiveStatusUpdate(update));
    updateActiveStatusUpdate(update);
  };

  const onDelete = () => {
    dispatch(
      deleteStatusUpdate({
        statusUpdate: activeStatusUpdate as StatusUpdate,
        onSuccess: showDeleteSuccessToast,
      }),
    );
  };

  const handleCollaboratorChange = (
    selectedCollaborators: SquadPayload,
    currentUser,
  ) => {
    const update = {
      ...activeStatusUpdate,
      collaborators: [
        ...selectedCollaborators.users
          .map(({ userId }) => squad.users.find((u) => u.userId === userId))
          .filter(Boolean),
        currentUser,
      ],
    };

    dispatch(setActiveStatusUpdate(update));
    updateActiveStatusUpdate(update);
  };

  const statusOptions = (
    <Menu selectedKeys={currentStatus ? [currentStatus.key] : []}>
      {Object.keys(UserStatusUpdateStatuses)
        .filter(
          (status) =>
            UserStatusUpdateStatuses[status].key !==
            UserStatusUpdateStatuses.none.key,
        )
        .map((update) => {
          return (
            <Menu.Item
              key={UserStatusUpdateStatuses[update].key}
              onClick={handleMenuClick}
            >
              <Tag color={UserStatusUpdateStatuses[update].color}>
                {UserStatusUpdateStatuses[update].label}
              </Tag>
            </Menu.Item>
          );
        })}
    </Menu>
  );

  const collaborators = () => {
    return (
      <SquadPicker
        onChange={(selectedCollaborators: SquadPayload) =>
          handleCollaboratorChange(selectedCollaborators, user)
        }
        value={{
          users: activeCollaborators || [],
          teams: [] as unknown as Team[],
        }}
        options={[
          {
            label: '',
            options: fullCollaboratorsList.map((u) => userOption(u)),
          },
        ]}
        isDisabled={!canEdit}
      >
        {({ value: selected }) => {
          const selectedUsers = selected.filter(
            (_usr) =>
              Number(_usr.value.split('_').at(-1)) !==
              activeStatusUpdate.createdBy.userId,
          );

          return selectedUsers.length ? (
            <CollaboratorsTriggerButton type="text">
              <Trigger size="small" value={selectedUsers} />
            </CollaboratorsTriggerButton>
          ) : (
            <CollaboratorsTriggerButton type="link" icon={<UserAvatar16 />}>
              {addCollaborators}
            </CollaboratorsTriggerButton>
          );
        }}
      </SquadPicker>
    );
  };

  const updateTitleOnBlur = (data) => {
    const title = getTextFromHTML(data) || '';

    dispatch(setActiveStatusUpdateTitle(title));
    dispatch(
      updateStatusUpdateTitle({
        statusUpdateTitle: title,
      }),
    );
  };

  return (
    <>
      <EditorContainer
        isPublished={isPublished}
        isFullScreen={handleFullScreen.active}
      >
        {spotlights.length > 0 ? (
          <SpotlightWrapper>
            <SpotlightGroup
              spotlights={spotlights}
              onSpotlightClick={(pin) => onSpotlightClick?.(pin)}
            />
          </SpotlightWrapper>
        ) : null}
        <Form colon={false}>
          <FormItem
            label={!isPublished ? <Label>{`${subject}:`}</Label> : null}
          >
            <div className={isPublished ? styles.published_head : styles.head}>
              {isAiFillResolving ? (
                <AiFillPlaceholderSkeleton hideSkeleton />
              ) : isPublished ? (
                <Wrapper
                  isPublished={isPublished}
                  isFullScreen={handleFullScreen.active}
                >
                  {isPublished &&
                  currentStatus &&
                  currentStatus?.label?.toLowerCase() !==
                    StatusUpdateStatuses.none.key ? (
                    <Tag color={currentStatus.color}>{currentStatus.label}</Tag>
                  ) : null}
                  {canCreate ? (
                    <EditorHeading
                      isPublished={isPublished}
                      readOnly={
                        activeStatusUpdate?.state ===
                          StatusUpdateState.Published || !canEdit
                      }
                      data-cy="status_update_heading"
                      value={activeStatusUpdate?.statusUpdateTitle}
                      autoSize={{ minRows: 1, maxRows: 6 }}
                    />
                  ) : null}
                </Wrapper>
              ) : canCreate ? (
                <RichTextEditor
                  data={activeStatusUpdate.statusUpdateTitle}
                  projectId={projectId}
                  onBlur={updateTitleOnBlur}
                  placeholder={documentTitlePlaceholder}
                  disallowNewLine
                  disallowMentions
                  disallowImages
                  autoFocus
                  noToolbar
                  noFormatting
                  testId="status_update_heading"
                />
              ) : null}

              {isPublished ? (
                <>
                  <StatusUpdateFullscreenToggle
                    isFullScreen={handleFullScreen.active}
                    onExitFullScreen={handleFullScreen.exit}
                    onFullScreen={handleFullScreen.enter}
                  />
                  <CopyUrlToClipboard />
                  {!handleFullScreen.active ? (
                    <MoreActions
                      titleNode={translatedDeleteConfirmationMessageTitle}
                      contentNode={translatedDeleteConfirmationMessageContent}
                      data={activeStatusUpdate}
                      showModalAlert
                      onDelete={onDelete}
                      disabledAction={
                        !activeStatusUpdate?.statusUpdateId || !canDelete
                      }
                    />
                  ) : null}
                </>
              ) : null}
            </div>
          </FormItem>

          {!isPublished ? (
            <FormItem label={!isPublished ? <Label>{`${to}:`}</Label> : null}>
              <SUAssigneePicker
                projectId={projectId}
                value={toUsers}
                onChange={handleToUsers}
                isPrivate={activeStatusUpdate.private}
                isDisabled={!canEdit}
              />
            </FormItem>
          ) : null}
          {!isPublished ? (
            <FormItem label={!isPublished ? <Label>{`${Cc}:`}</Label> : null}>
              <StatusUpdateCC
                userEmails={userEmails}
                setUserEmails={setUserEmails}
                isDisabled={!canEdit}
              />
            </FormItem>
          ) : !handleFullScreen.active ? (
            <SentToInfo projectId={projectId} />
          ) : null}
        </Form>
      </EditorContainer>

      <SubHeader isFullScreen={handleFullScreen.active}>
        {!isPublished ? (
          <FlexWrapper>
            <Dropdown
              trigger={['click']}
              overlay={statusOptions}
              disabled={!canEdit}
            >
              {currentStatus &&
              currentStatus?.label?.toLowerCase() !==
                StatusUpdateStatuses.none.key ? (
                <Tag color={currentStatus.color}>
                  {currentStatus.label}{' '}
                  <>{canEdit ? <CloseIcon onClick={removeStatus} /> : null}</>
                </Tag>
              ) : (
                <AddStatusButton size="small" type="link" disabled={!canEdit}>
                  {addStatus}
                </AddStatusButton>
              )}
            </Dropdown>
            <div className={styles.collaborators}>{collaborators()}</div>
          </FlexWrapper>
        ) : !handleFullScreen.active ? (
          <AddMailWrapper>
            <CCLabel>{`${Cc} :`}</CCLabel>
            <StatusUpdateCC
              userEmails={userEmails}
              setUserEmails={setUserEmails}
              isDisabled={!canEdit}
            />
          </AddMailWrapper>
        ) : null}
      </SubHeader>

      {activeStatusUpdate?.private ? (
        <PrivatePlacard>
          <FormattedMessage
            id="status_updates.private.label"
            values={{
              accountName: userInfo.company.companyName,
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </PrivatePlacard>
      ) : null}
    </>
  );
};
